/* eslint-disable no-useless-computed-key */
/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Col, Row, Input, Label, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import utils from '../../../../../lib/utils';
import useEpikBoxPri from './useEpikBoxPri';
import './EpikboxPri.css';
import { setPriFailoversList, getPriStatus, updateEpikBox, updateSipPri, getBoxDetailById } from '../../../../../actions/EpikBoxes';
import Toggle from '../../../../Toggle';
import ConfirmationModal from '../../../../ConfirmationModal';
import MultiSelectPriDropdown from '../../../../MultiSelectPriDropdown';
import AddSecurityCodeModal from '../../../../AddSecurityCodeModal';
import { ReactComponent as PriStatus } from '../../../../../Res/svg/PRI_Status_Check.svg'
import TooltipCustom from '../../TooltipCustom';
import CustomModal from '../../../../CustomModal';
import InfoPopup from '../../../../InfoPopup';
import logoLoading from '../../../../../Res/logo-loading.gif';
import addBoxAction from '../../../../../lib/api/addBoxAction';
import addNote from '../../../../../lib/api/addNote';

const CAS_DEFAULTS = {
  cas_signalling_type: 'em_i',
  cas_digit_timeout: 3000,
  cas_max_digits: 11,
  cas_dial_timeout: 15000,
  cas_answer_supervision: false,
  cas_answer_ms: 500,
  cas_hangup_ms: 1200,
  cas_release_guard_time_ms: 1000,
  cas_wink_ms: 150,
  cas_flash_ms: 750,
  cas_receive_wink_ms: 300,
};

const CAS_TOOLTIPS = {
  cas_digit_timeout:
    'Maximum time to wait between received DTMF/MF digits before considering dialing complete or timed out.',
  cas_max_digits: 'The maximum number of digits the system will collect for a dialed number',
  cas_dial_timeout: 'Maximum time allowed for dialing to complete before the call attempt is abandoned.',
  cas_answer_supervision:
    'When enabled, the system relies on an answer signal from the far end to mark the call as answered.',
  cas_answer_ms: "Minimum duration a CAS answer signal must be present before it's accepted",
  cas_hangup_ms: "Minimum duration a CAS on-hook/disconnect signal must be present before it's accepted",
  cas_release_guard_time_ms:
    'Time the channel holds in a guarded/idle state after a call ends before it can be reseized for a new call.',
  cas_wink_ms:
    "Duration of the wink pulse the system sends to signal the far end that it's ready to receive.",
  cas_flash_ms:
    'Duration of the hook-flash pulse the system sends to trigger a supplementary feature (e.g., transfer or hold) on the far end.',
  cas_receive_wink_ms:
    'Expected duration window used to detect and validate an incoming wink pulse from the far end as a genuine signal (not noise).',
};

const toCasNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseSignallingType = value => {
  return `${value || ''}`.toLowerCase() === 'em_w' ? 'em_w' : 'em_i';
};

const parseAnswerSupervision = value =>
  value === true || value === 'true' || value === 'True' || value === 1;

const getCasConfigFromSettings = (settings = {}) => ({
  cas_signalling_type: parseSignallingType(settings.cas_signalling_type),
  cas_digit_timeout: toCasNumber(settings.cas_digit_timeout, CAS_DEFAULTS.cas_digit_timeout),
  cas_max_digits: toCasNumber(settings.cas_max_digits, CAS_DEFAULTS.cas_max_digits),
  cas_dial_timeout: toCasNumber(settings.cas_dial_timeout, CAS_DEFAULTS.cas_dial_timeout),
  cas_answer_supervision: parseAnswerSupervision(settings.cas_answer_supervision),
  cas_answer_ms: toCasNumber(settings.cas_answer_ms, CAS_DEFAULTS.cas_answer_ms),
  cas_hangup_ms: toCasNumber(settings.cas_hangup_ms, CAS_DEFAULTS.cas_hangup_ms),
  cas_release_guard_time_ms: toCasNumber(
    settings.cas_release_guard_time_ms,
    CAS_DEFAULTS.cas_release_guard_time_ms,
  ),
  cas_wink_ms: toCasNumber(settings.cas_wink_ms, CAS_DEFAULTS.cas_wink_ms),
  cas_flash_ms: toCasNumber(settings.cas_flash_ms, CAS_DEFAULTS.cas_flash_ms),
  cas_receive_wink_ms: toCasNumber(settings.cas_receive_wink_ms, CAS_DEFAULTS.cas_receive_wink_ms),
});

const CasAdvancedField = ({ label, tipId, tooltip, children }) => (
  <div className="cas-advanced-row">
    <Label className="sip-input-label cas-advanced-label">
      {label}
      <InfoPopup id={tipId} text={tooltip} />
    </Label>
    <div className="cas-advanced-input">{children}</div>
  </div>
);

export default function EpikBoxPri({ priSipStatus, setDcUpdatePopup, type = 'pri' }) {
  const [flag, setFlag] = useState(false);
  const box = useSelector(state => state.AppState.boxDetails.box);
  const priSettings = useSelector(state => state.AppState.priSipData?.priSettingData);
  const loggedInUser = useSelector(state => state.AppState.user);
  const companyPriBoxes = useSelector(state => state.UIState.companies.companyPriBoxes);

  const [numberOfChannel] = useState(24);
  const [dchannel, setDchannel] = useState('');
  const [bchannel, setBchannel] = useState('');
  const [emChannels, setEmChannels] = useState('1-24');
  const [dchannelArray, setDchannelArray] = useState([]);
  const [channelBValidValues, setChannelBValidValues] = useState([]);
  const [channelBSubset, setChannelBSubset] = useState([]);
  const [channelRange, setChannelRange] = useState(type === 'cas' ? '1-24' : '1-23');
  const [channelRangeExpanded, setChannelRangeExpanded] = useState([]);
  const [echoCancellation, setEchoCancellation] = useState(false);
  const [defaultPri, setDefaultPri] = useState(false);
  const [warningModal, setWarningModal] = useState(false);
  const [priCompaniesList, setPriCompaniesList] = useState([]);
  const [priFailovers, setPriFailovers] = useState([]);
  const [preSelectedPriList, setPreSelectedPriList] = useState([]);
  const [priToggle, setPriToggle] = useState(false);
  const [securityModal, setSecurityModal] = useState(false);
  const [sortedPriList, setSortedPriList] = useState([]);
  const [casConfig, setCasConfig] = useState(CAS_DEFAULTS);
  const [fsRestartRequired, setFsRestartRequired] = useState(false);


  const defaultEnabledStates = {
    dchannelInput: false,
    bchannel: false,
    channelRange: false,
    numberOfChannel: false,
  };
  const [isEnableInput, setIsEnableInput] = useState(defaultEnabledStates);
  const [priStatusModalOpen, setPriStatusModalOpen] = useState(false);
  const [priLoading, setPriLoading] = useState(false);
  const [priStatus, setPriStatus] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const isCasType = type === 'cas';
  const settingsLabel = isCasType ? 'CAS' : 'PRI';

  const dispatch = useDispatch();
  const token = localStorage.getItem('jwtToken') || null;

  useEffect(() => {
    if (companyPriBoxes?.length > 0) {
      setPriCompaniesList(companyPriBoxes);
    }
  }, [companyPriBoxes]);

  const handleBChannelChange = value => {
    const regex = /^[-,0-9]+$/;
    if (value.match(regex) || !value) {
      const expandedArray = utils.validateChannelInput(value);
      setChannelBValidValues(expandedArray);
      const dChannel = !isCasType && dchannel ? [+dchannel] : [];
      setDchannelArray(dChannel);
      if (Math.max(...expandedArray) <= +numberOfChannel) {
        if (!isCasType && utils.compareArrays(expandedArray, dChannel) && value) {
          toast.error(`${dchannel} is already added in D Channel`);
        } else if (expandedArray?.length + dChannel?.length > +numberOfChannel) {
          toast.error(`Number of channels exceded from Range of Channels`);
        } else {
          setBchannel(value);
          if (value) {
            setIsEnableInput({
              ...defaultEnabledStates,
              bchannel: true,
              channelRange: true,
            });
          }
        }
      } else {
        toast.error(`Channel value should be less than Number of Channels`);
      }
    }
  };

  const handleDchannel = value => {
    if (isCasType) return;
    const regex = /^[0-9]+$/;
    if (value.match(regex) || !value) {
      const expandedArray = utils.validateChannelInput(bchannel);
      const dChannel = value ? [+value] : [];
      if (utils.compareArrays(expandedArray, dChannel)) {
        toast.error(`${value} is already added in D Channel`);
      } else if (value <= +numberOfChannel) {
        setDchannel(value);
        if (value) {
          setIsEnableInput({
            ...defaultEnabledStates,
            bchannel: true,
            dchannelInput: true,
          });
        }
      } else {
        toast.error(`Number of Channels should be in the range of 1-24`);
      }
    }
  };

  const handleChannelRange = value => {
    const regex = /^[-,0-9]+$/;
    if (value.match(regex) || !value) {
      const expandedArray = utils.validateChannelInput(value);
      setChannelRangeExpanded(expandedArray);
      const filterArry = channelBValidValues.filter(r => expandedArray.includes(r));
      setChannelBSubset(JSON.stringify(filterArry) === JSON.stringify(expandedArray));
      setChannelRange(value);
    }
  };

  const handleEmChannelsChange = value => {
    const regex = /^[-,0-9]+$/;
    if (value.match(regex) || !value) {
      setEmChannels(value);
    }
  };

  const startOver = () => {
    setIsEnableInput({
      ...defaultEnabledStates,
      dchannelInput: true,
    });
    setDchannel('');
    setBchannel('');
    setEmChannels('1-24');
    setChannelRange(isCasType ? '1-24' : '1-23');
    setCasConfig(CAS_DEFAULTS);
    setDefaultPri(true);
  };

  const handleCasConfigChange = (name, value) => {
    setCasConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleCasNumericChange = (name, value) => {
    if (value === '') {
      handleCasConfigChange(name, '');
      return;
    }
    if (/^\d+$/.test(value)) {
      handleCasConfigChange(name, Number(value));
    }
  };

  const { produceState, dropDownStates, handleDropDownStates } = useEpikBoxPri(
    numberOfChannel,
    dchannel,
    bchannel,
    channelRange,
    echoCancellation,
    defaultPri,
    priFailovers,
    sortedPriList
  );

  const onSubmit = async e => {
    const ispri = true;
    e.preventDefault();
    setSaveLoading(true);
    
    try {
      if (!isCasType && (!dchannel?.trim() || !bchannel?.trim())) {
        toast.error('D Channel and B Channel are required for PRI');
        return;
      }
      if (isCasType && !emChannels?.trim()) {
        toast.error('EM Channels is required for CAS');
        return;
      }
      const derivedChannelRange = isCasType ? emChannels : bchannel;

      const newState = produceState();
      const priSettingsUpdated = { ...priSettings, ...newState };
      let priNumberSettings = {
        ...priSettingsUpdated,
        numberSettings: priSettings?.numberSettings || [],
        echoChannels: derivedChannelRange,
        channelRange: derivedChannelRange,
        type,
      };
      if (isCasType) {
        const savedCasConfig = getCasConfigFromSettings(priSettings);
        const winkTimingChanged =
          toCasNumber(casConfig.cas_wink_ms, CAS_DEFAULTS.cas_wink_ms) !== savedCasConfig.cas_wink_ms ||
          toCasNumber(casConfig.cas_flash_ms, CAS_DEFAULTS.cas_flash_ms) !== savedCasConfig.cas_flash_ms ||
          toCasNumber(casConfig.cas_receive_wink_ms, CAS_DEFAULTS.cas_receive_wink_ms) !==
            savedCasConfig.cas_receive_wink_ms;

        priNumberSettings = {
          ...priNumberSettings,
          dChannel: '',
          bChannels: '',
          em_channels: emChannels || '1-24',
          cas_signalling_type: parseSignallingType(casConfig.cas_signalling_type),
          cas_digit_timeout: toCasNumber(casConfig.cas_digit_timeout, CAS_DEFAULTS.cas_digit_timeout),
          cas_max_digits: toCasNumber(casConfig.cas_max_digits, CAS_DEFAULTS.cas_max_digits),
          cas_dial_timeout: toCasNumber(casConfig.cas_dial_timeout, CAS_DEFAULTS.cas_dial_timeout),
          cas_answer_supervision: !!casConfig.cas_answer_supervision,
          cas_answer_ms: toCasNumber(casConfig.cas_answer_ms, CAS_DEFAULTS.cas_answer_ms),
          cas_hangup_ms: toCasNumber(casConfig.cas_hangup_ms, CAS_DEFAULTS.cas_hangup_ms),
          cas_release_guard_time_ms: toCasNumber(
            casConfig.cas_release_guard_time_ms,
            CAS_DEFAULTS.cas_release_guard_time_ms,
          ),
          cas_wink_ms: toCasNumber(casConfig.cas_wink_ms, CAS_DEFAULTS.cas_wink_ms),
          cas_flash_ms: toCasNumber(casConfig.cas_flash_ms, CAS_DEFAULTS.cas_flash_ms),
          cas_receive_wink_ms: toCasNumber(casConfig.cas_receive_wink_ms, CAS_DEFAULTS.cas_receive_wink_ms),
          restartFreeswitch: winkTimingChanged,
        };
        setFsRestartRequired(winkTimingChanged);
      }

      if (priToggle) {
        if (priNumberSettings.priFailovers?.length <= 0) {
          toast.error('Select atleast One failover backup');
          setSaveLoading(false);
          return
        }
      } else {
        priNumberSettings = { ...priNumberSettings, priFailovers: [] }
      }
      
      let sipPriResponse;

      if (flag) {
        const usedChannelsCount = isCasType
          ? channelBValidValues?.length
          : dchannelArray?.length + channelBValidValues?.length;
        if (usedChannelsCount === +numberOfChannel) {
          if (channelBSubset) {
            if (bchannel?.length) {
              setIsEnableInput({
                ...defaultEnabledStates,
              });
              
              sipPriResponse = await dispatch(updateSipPri(token, box?._id, ispri, priNumberSettings));
              await dispatch(getBoxDetailById(token, box));
            } else {
              toast.error(`B channel is empty`);
            }
          } else {
            toast.error(
              `${channelRangeExpanded?.toString()} is not subset of ${channelBValidValues?.toString()}`,
            );
          }
        } else {
          toast.error(
            isCasType
              ? `B channel is not equal to Number of Channels`
              : `D channel and B channel is not equal to Number of Channels`,
          );
        }
      }
      if (!flag) {
        sipPriResponse = await dispatch(updateSipPri(token, box?._id, ispri, priNumberSettings));
        await dispatch(getBoxDetailById(token, box));
      }

      if (sipPriResponse) {
        addBoxAction({ boxID: box?._id, activity: `updated ${settingsLabel} details` });
        addNote({ boxID: box?._id, note: `${settingsLabel} details updated`});
      }

      setWarningModal(true);
      setIsEnableInput(defaultEnabledStates);
      setPreSelectedPriList([])
    } catch (error) {
      console.error('Error saving PRI details:', error);
      toast.error(`Failed to save ${settingsLabel} details`);
    } finally {
      setSaveLoading(false);
    }
  };

  const setPreSelectedList = (list) => {
    console.log({ list, companyPriBoxes })
    const preSelectedList = [];
    list?.forEach(item => {
      const found = companyPriBoxes?.find(box => box?.serialNumber === item);
      if (found) {
        preSelectedList.push(found);
      }
    });
    console.log('setPreSelectedList preSelectedList :::', preSelectedList);
    setPreSelectedPriList(preSelectedList);
  }

  useEffect(() => {
    if (priSettings && Object?.entries(priSettings).length) {
      setDchannel(priSettings?.dChannel || '');
      setBchannel(priSettings?.bChannels || '');
      setEmChannels(priSettings?.em_channels || '1-24');
      setChannelRange(isCasType ? '1-24' : (priSettings?.echoChannels || '1-23'));
      setEchoCancellation(priSettings?.echoCancellation || false);
      setPriFailovers(priSettings?.priFailovers || []);
      if (isCasType) {
        setCasConfig(getCasConfigFromSettings(priSettings));
      }
      if (priSettings?.priFailovers?.length > 0) {
        setPreSelectedList(priSettings?.priFailovers);
        setPriToggle(true)
      }
    }
  }, [priSettings, isCasType]);

  //remove this code after PRI failover backup testing
  useEffect(() => {
    // if (Object?.entries(box?.priSettings).length) {
    //   setDchannel(box?.priSettings?.dChannel || '');
    //   setBchannel(box?.priSettings?.bChannels || '');
    //   setChannelRange(box?.priSettings?.echoChannels || '');
    //   setEchoCancellation(box?.priSettings?.echoCancellation || false);
    //   setPriFailovers(box?.priSettings?.priFailovers || []);
    //   if (box?.priSettings?.priFailovers?.length > 0) {
    //     setPreSelectedList(box?.priSettings?.priFailovers);
    //   }
    // }
    // console.log('priSettings', box.priSettings)
  }, [box]);


  const onMouseOver = () => {
    setDefaultPri(false);
  };

  const onPriChange = async (list) => {
    await dispatch(setPriFailoversList(list));
    const listNumbers = list?.reduce((acc, curr) => {
      acc.push(curr.serialNumber);
      return acc;
    }, []);

    setPriFailovers(listNumbers);
  }

  const onTogglePri = (e) => {
    setPriToggle(!e);
    // if (!e) {
    //   setSecurityModal(true);
    // } else {
    //   setPriToggle(false);
    // }
  }

  const onSecurityConfirm = () => {
    setPriToggle(!priToggle);
    setSecurityModal(false);
  }

  const sortedDataList = (data) => {
    setSortedPriList(data);
  }

  const onPriStatusCheck = async () => {
    setPriStatusModalOpen(true);
    setPriLoading(true);
    const result = await dispatch(getPriStatus(token, box?.vpnAddress));
    if (result && result?.response[0]) {
      setPriStatus(result?.response[0] === 'UP' ? 'Passed' : 'Failed');
    } else {
      setPriStatus('N/A');
    }
    setPriLoading(false);
    console.log(result);
  }

  return (
    <div className="priContainer">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <p className="heading">{settingsLabel} Settings</p>
        <div className='d-flex align-items-center'>
          <PriStatus
            onClick={onPriStatusCheck}
            id='priSipStatus' width={30} height={30} className='mr-2 cursor-pointer' />
          <TooltipCustom tipId="priSipStatus" displayData={`${settingsLabel} Status Check`} placement="top" />
        </div>

      </div>
      <div className="sip-section ">
        <Row className="sip-input ">
          <Col sm={6} md={4}>
            <Label for="name" className="sip-input-label">
              Number of Channels
            </Label>
          </Col>
          <Col sm={6} md={5}>
            <Input required value={numberOfChannel} disabled type="text" name="numberOfChannel" />
          </Col>
        </Row>
      </div>
      <Row style={{ paddingTop: '5px' }}>
        <Col sm={6} md={4}>
          <Label className="sip-input-label">T1 Framing</Label>
        </Col>
        <Col sm={6} md={5}>
          <Input
            type="select"
            value={dropDownStates.framing}
            onChange={e => handleDropDownStates('framing', e.currentTarget.value)}
          >
            <option key="d4" value="d4">
              d4
            </option>
            <option key="esf" value="esf">
              esf
            </option>
            <option key="cas" value="cas">
              cas
            </option>
          </Input>
        </Col>
      </Row>
      <Row style={{ paddingTop: '5px' }}>
        <Col sm={6} md={4}>
          <Label className="sip-input-label">Line Code</Label>
        </Col>
        <Col sm={6} md={5}>
          <Input
            type="select"
            value={dropDownStates.lineCode}
            onChange={e => handleDropDownStates('lineCode', e.currentTarget.value)}
          >
            <option key="b8zs" value="b8zs ">
              b8zs
            </option>
            <option key="ami" value="ami">
              ami
            </option>
          </Input>
        </Col>
      </Row>
      <Row style={{ paddingTop: '5px' }}>
        <Col sm={6} md={4}>
          <Label className="sip-input-label">Timing Source</Label>
        </Col>
        <Col sm={6} md={5}>
          <Input
            type="select"
            value={dropDownStates.timeAndSource}
            onChange={e => handleDropDownStates('timeAndSource', e.currentTarget.value)}
          >
            <option key="internal" value="internal">
              Internal
            </option>
            <option key="external" value="external">
              External
            </option>
          </Input>
        </Col>
      </Row>
      {!isCasType && (
        <div className="sip-section ">
          <Row className="sip-input ">
            <Col sm={6} md={4}>
              <Label for="name" className="sip-input-label">
                D Channel
              </Label>
            </Col>
            <Col sm={6} md={5}>
              <Input
                onChange={e => handleDchannel(e.target.value)}
                required
                onFocus={() => {
                  if (numberOfChannel) {
                    setIsEnableInput({ ...isEnableInput, numberOfChannel: false });
                  }
                }}
                value={dchannel}
                // disabled={!isEnableInput?.dchannelInput}
                type="text"
                name="name"
              />
            </Col>
          </Row>
        </div>
      )}
      {!isCasType && (
        <div className="sip-section ">
          <Row className="sip-input ">
            <Col sm={6} md={4}>
              <Label for="name" className="sip-input-label">
                B Channel
              </Label>
            </Col>
            <Col sm={6} md={5}>
              <Input
                onChange={e => handleBChannelChange(e.target.value)}
                required
                onFocus={() => {
                  if (dchannel) {
                    setIsEnableInput({ ...isEnableInput, dchannelInput: false });
                  }
                }}
                // disabled={!isEnableInput?.bchannel}
                value={bchannel}
                type="text"
                name="name"
              />
            </Col>
          </Row>
        </div>
      )}
      {isCasType && (
        <div className="sip-section ">
          <Row className="sip-input ">
            <Col sm={6} md={4}>
              <Label for="name" className="sip-input-label">
                EM Channels
              </Label>
            </Col>
            <Col sm={6} md={5}>
              <Input
                onChange={e => handleEmChannelsChange(e.target.value)}
                required
                value={emChannels}
                type="text"
                name="em_channels"
              />
            </Col>
          </Row>
        </div>
      )}
      {isCasType && (
        <Row style={{ paddingTop: '5px' }}>
          <Col sm={6} md={4}>
            <Label className="sip-input-label">Start Method</Label>
          </Col>
          <Col sm={6} md={5}>
            <Input
              type="select"
              value={casConfig.cas_signalling_type}
              onChange={e => handleCasConfigChange('cas_signalling_type', e.currentTarget.value)}
            >
              <option value="em_i">Immediate</option>
              <option value="em_w">Wink</option>
            </Input>
          </Col>
        </Row>
      )}
      {!isCasType && (
        <Row style={{ paddingTop: '5px' }}>
          <Col sm={6} md={4}>
            <Label className="sip-input-label">Switch Type</Label>
          </Col>
          <Col sm={6} md={5}>
            <Input
              type="select"
              value={dropDownStates.switchType}
              onChange={e => handleDropDownStates('switchType', e.currentTarget.value)}
            >
              <option key="national" value="national">
                national
              </option>
              <option key="ni2" value="ni2">
                ni2
              </option>
              <option key="4ess" value="4ess">
                4ess
              </option>
              <option key="5ess" value="5ess">
                5ess
              </option>
              <option key="qsig" value="qsig">
                qsig
              </option>
              <option key="euroisdn" value="euroisdn">
                euroisdn
              </option>
              <option key="ntt" value="ntt">
                ntt
              </option>
            </Input>
          </Col>
        </Row>
      )}
      <div className="sip-section ">
        <Row className="sip-input ">
          <Col sm={6} md={4}>
            <Label for="name" className="sip-input-label">
              Echo Cancellation
            </Label>
          </Col>
          <Col sm={6} md={5}>
            <Toggle
              value={echoCancellation}
              onToggle={e => {
                setEchoCancellation(!e);
              }}
            />
          </Col>
        </Row>
      </div>

      <div className="sip-section ">
        <Row className="sip-input ">
          <Col sm={6} md={4}>
            <Label for="name" className="sip-input-label">
              Channel Range
            </Label>
          </Col>
          <Col sm={6} md={5}>
            <Input
              onChange={e => handleChannelRange(e.target.value)}
              required
              onFocus={() => {
                if (bchannel) {
                  setIsEnableInput({ ...isEnableInput, bChannels: false });
                }
              }}
              value={channelRange}
              type="text"
              name="name"
              disabled={isCasType}
            />
          </Col>
        </Row>
      </div>
      <Row style={{ paddingTop: '5px', marginBottom: '15px' }}>
        <Col sm={6} md={4}>
          <Label className="sip-input-label">Echo Cancelation Type</Label>
        </Col>
        <Col sm={6} md={5}>
          <Input
            required
            value={dropDownStates.echoCancellationType}
            type="text"
            name="echoCancelationType"
            disabled
          />
        </Col>
      </Row>
      <Row style={{ paddingTop: '5px', marginBottom: '15px' }}>
        <Col sm={6} md={4}>
          <Label className="pri-input-label bold">{settingsLabel} Failover</Label>
        </Col>
        <Col sm={6} md={5}>
          <Toggle
            value={priToggle}
            onToggle={(e) => onTogglePri(e)}
          />
        </Col>
      </Row>
      {
        priToggle ?
          <Row style={{ paddingTop: '5px', marginBottom: '15px' }}>
            <Col sm={6} md={4}>
              <Label className="sip-input-label underline">{`Select Backup ${settingsLabel}`}</Label>
            </Col>
            <Col sm={6} md={5}>
              <MultiSelectPriDropdown
                MultiSelect
                nameTag="serialNumber"
                filterTag="serialNumber"
                className="testing-filter"
                placeholder={
                  !(priCompaniesList?.length > 0)
                    ? 'No Boxes Found'
                    : 'Select Box Serial Number'
                }
                dropdownList={priCompaniesList}
                showFirstListElement
                onChange={onPriChange}
                initialList={preSelectedPriList}
                sortedData={sortedDataList}
              />
            </Col>
          </Row>
          : null
      }

      {isCasType && (
        <div className="cas-advanced-section">
          <p className="heading cas-advanced-heading">Advanced Configuration Options</p>
          <Row>
            <Col md={6}>
              <CasAdvancedField
                label="digit-timeout"
                tipId="cas-digit-timeout"
                tooltip={CAS_TOOLTIPS.cas_digit_timeout}
              >
                <Input
                  type="number"
                  min="0"
                  name="cas_digit_timeout"
                  value={casConfig.cas_digit_timeout}
                  onChange={e => handleCasNumericChange('cas_digit_timeout', e.target.value)}
                />
              </CasAdvancedField>
              <CasAdvancedField
                label="max-digits"
                tipId="cas-max-digits"
                tooltip={CAS_TOOLTIPS.cas_max_digits}
              >
                <Input
                  type="number"
                  min="0"
                  name="cas_max_digits"
                  value={casConfig.cas_max_digits}
                  onChange={e => handleCasNumericChange('cas_max_digits', e.target.value)}
                />
              </CasAdvancedField>
              <CasAdvancedField
                label="dial-timeout"
                tipId="cas-dial-timeout"
                tooltip={CAS_TOOLTIPS.cas_dial_timeout}
              >
                <Input
                  type="number"
                  min="0"
                  name="cas_dial_timeout"
                  value={casConfig.cas_dial_timeout}
                  onChange={e => handleCasNumericChange('cas_dial_timeout', e.target.value)}
                />
              </CasAdvancedField>
              <CasAdvancedField
                label="answer-supervision"
                tipId="cas-answer-supervision"
                tooltip={CAS_TOOLTIPS.cas_answer_supervision}
              >
                <Input
                  type="select"
                  name="cas_answer_supervision"
                  value={casConfig.cas_answer_supervision ? 'true' : 'false'}
                  onChange={e =>
                    handleCasConfigChange('cas_answer_supervision', e.target.value === 'true')
                  }
                >
                  <option value="false">False</option>
                  <option value="true">True</option>
                </Input>
              </CasAdvancedField>
              {!!casConfig.cas_answer_supervision && (
                <>
                  <CasAdvancedField
                    label="cas-answer-ms"
                    tipId="cas-answer-ms"
                    tooltip={CAS_TOOLTIPS.cas_answer_ms}
                  >
                    <Input
                      type="number"
                      min="0"
                      name="cas_answer_ms"
                      value={casConfig.cas_answer_ms}
                      onChange={e => handleCasNumericChange('cas_answer_ms', e.target.value)}
                    />
                  </CasAdvancedField>
                  <CasAdvancedField
                    label="cas-hangup-ms"
                    tipId="cas-hangup-ms"
                    tooltip={CAS_TOOLTIPS.cas_hangup_ms}
                  >
                    <Input
                      type="number"
                      min="0"
                      name="cas_hangup_ms"
                      value={casConfig.cas_hangup_ms}
                      onChange={e => handleCasNumericChange('cas_hangup_ms', e.target.value)}
                    />
                  </CasAdvancedField>
                </>
              )}
            </Col>
            <Col md={6}>
              <CasAdvancedField
                label="release-guard-time-ms"
                tipId="cas-release-guard-time-ms"
                tooltip={CAS_TOOLTIPS.cas_release_guard_time_ms}
              >
                <Input
                  type="number"
                  min="0"
                  name="cas_release_guard_time_ms"
                  value={casConfig.cas_release_guard_time_ms}
                  onChange={e => handleCasNumericChange('cas_release_guard_time_ms', e.target.value)}
                />
              </CasAdvancedField>
              <CasAdvancedField
                label="wink_ms (transmitted wink)"
                tipId="cas-wink-ms"
                tooltip={CAS_TOOLTIPS.cas_wink_ms}
              >
                <Input
                  type="number"
                  min="0"
                  name="cas_wink_ms"
                  value={casConfig.cas_wink_ms}
                  onChange={e => handleCasNumericChange('cas_wink_ms', e.target.value)}
                />
              </CasAdvancedField>
              <CasAdvancedField
                label="flash_ms (transmitted flash)"
                tipId="cas-flash-ms"
                tooltip={CAS_TOOLTIPS.cas_flash_ms}
              >
                <Input
                  type="number"
                  min="0"
                  name="cas_flash_ms"
                  value={casConfig.cas_flash_ms}
                  onChange={e => handleCasNumericChange('cas_flash_ms', e.target.value)}
                />
              </CasAdvancedField>
              <CasAdvancedField
                label="receive_wink_ms"
                tipId="cas-receive-wink-ms"
                tooltip={CAS_TOOLTIPS.cas_receive_wink_ms}
              >
                <Input
                  type="number"
                  min="0"
                  name="cas_receive_wink_ms"
                  value={casConfig.cas_receive_wink_ms}
                  onChange={e => handleCasNumericChange('cas_receive_wink_ms', e.target.value)}
                />
              </CasAdvancedField>
            </Col>
          </Row>
        </div>
      )}

      <Row
        justify="space-between">
        <Button
          onMouseDown={onMouseOver}
          color="primary"
          title="This will erase all channel ranges"
          onClick={startOver}
        >
          Reset Settings
        </Button>
        <Col className="pri-save-btn">
          <Button  
            color="primary"  
            disabled={saveLoading}
            onClick={ (e) => {
              const divElement = document.getElementById('test-time-remaining');
              const isHidden = divElement.getAttribute('hidden') !== null;
              if (isHidden) {
                setDcUpdatePopup({ show: true });
              } else {
                onSubmit(e);
              }
            }}
          >
            {saveLoading ? (
              <>
                <img src={logoLoading} className="loading-logo-sm mr-2" alt="loading" style={{ width: '16px', height: '16px' }} />
                Saving...
              </>
            ) : (
              `Save ${settingsLabel} Detail`
            )}
          </Button>
        </Col>
      </Row>
      <CustomModal
        header={`${settingsLabel} Status Check`}
        isOpen={priStatusModalOpen}
        onCancel={() => setPriStatusModalOpen(false)}
        close
        medium
      >
        <div className='align-items-center justify-content-center d-flex'>
          {
            priLoading ?
              <img src={logoLoading} className="loading-logo-sm" alt="loading" />
              :
              <label className='header-pri-text mt-4 mb-4'>
                {settingsLabel} Status :
                <span className={`ml-1 ${priStatus === 'UP' ? "text-success" : "text-danger"} bold`}>{priStatus}{!priSettings?.isSynced && " / database not synced"}</span>
              </label>
          }
        </div>
      </CustomModal>
      <ConfirmationModal
        bodyText={
          fsRestartRequired
            ? 'Please reboot this device for changes to take effect. FreeSWITCH will be restarted to apply wink and flash timing changes.'
            : 'Please reboot this device for changes to take effect.'
        }
        isOpen={warningModal}
        onCancel={() => setWarningModal(false)}
        ignoreConfirmButton
        close
      />
      <AddSecurityCodeModal
        isOpen={securityModal}
        onCancel={() => setSecurityModal(false)}
        onConfirm={onSecurityConfirm}
        featureName={isCasType ? 'casFailover' : 'priFailover'}
      />
    </div>
  );
}
